import { apiService, API_ENDPOINTS } from '../config/api';
import type { SupportRequestPayload, SupportTicketReceipt, SupportTicket, SupportTicketPage, SupportMessage, SupportMessagePage } from '../types';

export const SUPPORT_EMAIL = 'admin@studysyncs.com';

export class SupportService {
  static async listAdminTickets(status?: SupportTicketReceipt['status'], cursor?: string): Promise<SupportTicketPage> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (cursor) params.set('cursor', cursor);
    const result = await apiService.get(`${API_ENDPOINTS.SUPPORT.ADMIN_TICKETS}?${params}`);
    if (result?.success !== true || !Array.isArray(result.tickets)) throw new Error('We could not load the inbox. Please try again.');
    return result;
  }

  static async listMessages(id: string, cursor?: string): Promise<SupportMessagePage> {
    const result = await apiService.get(API_ENDPOINTS.SUPPORT.MESSAGES(id) + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''));
    if (result?.success !== true || !Array.isArray(result.messages)) throw new Error('We could not load the replies. Please try again.');
    return result;
  }

  static async reply(id: string, body: string, requestId: string, admin = false): Promise<{ message: SupportMessage; ticket: SupportTicketReceipt }> {
    const result = await apiService.post(API_ENDPOINTS.SUPPORT.MESSAGES(id, admin), { body, requestId });
    if (result?.success !== true || !result.message?.id) throw new Error('We could not confirm your reply was saved. Please retry.');
    return result;
  }

  static async setStatus(id: string, status: SupportTicketReceipt['status'], requestId: string): Promise<SupportTicketReceipt> {
    const result = await apiService.patch(API_ENDPOINTS.SUPPORT.STATUS(id), { status, requestId });
    if (result?.success !== true || !result.ticket) throw new Error('We could not update the status. Please try again.');
    return result.ticket;
  }

  static async markRead(id: string, sequence: number, admin = false): Promise<void> {
    await apiService.post(API_ENDPOINTS.SUPPORT.READ(id, admin), { sequence });
  }

  static async listTickets(cursor?: string): Promise<SupportTicketPage> {
    const result = await apiService.get(API_ENDPOINTS.SUPPORT.TICKETS + (cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''));
    if (result?.success !== true || !Array.isArray(result.tickets)) throw new Error('We could not load your tickets. Please try again.');
    return result;
  }

  static async getTicket(id: string): Promise<SupportTicket> {
    const result = await apiService.get(API_ENDPOINTS.SUPPORT.TICKET(id));
    if (result?.success !== true || !result.ticket) throw new Error('We could not load this ticket. Please try again.');
    return result.ticket;
  }

  static async getScreenshot(id: string, index: number): Promise<string> {
    const result = await apiService.get(API_ENDPOINTS.SUPPORT.SCREENSHOT(id, index));
    const screenshot = result?.screenshot;
    if (result?.success !== true || !screenshot?.data || !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(screenshot.mimeType)) {
      throw new Error('We could not load this screenshot. Please try again.');
    }
    return `data:${screenshot.mimeType};base64,${screenshot.data}`;
  }

  static async submitSupportRequest(
    data: SupportRequestPayload
  ): Promise<{ success: true; ticket: SupportTicketReceipt }> {
    const result = await apiService.post(API_ENDPOINTS.SUPPORT.SUBMIT, data);
    if (result?.success !== true || !result?.ticket?.id) throw new Error(result?.error || 'We could not confirm your ticket was saved. Please retry this request.');
    return result;
  }
}
